<template>
  <main :class="b(modifiers)">
    <c-hero-banner
      :class="b('banner')"
      :image-initial-height="matchTheme(themeConfig.bannerImage.imageInitialHeight)"
      :image-position="matchTheme(themeConfig.bannerImage.position)"
      :image="bannerImage"
      :prefix="matchThemeTranslation(themeConfig.translations.subtitle)"
      :teaser-text="matchThemeTranslation(themeConfig.translations.teaserText)"
      :title="matchThemeTranslation(themeConfig.translations.title)"
      variant="header"
    />
    <div
      v-if="isTheme('sagemax')"
      :class="b('search-prefix')"
    >
      <h3 :class="b('search-prefix-title')">
        {{ $t('l-sagemax-electronic-instruction-list.searchByPrefix') }}
      </h3>
    </div>
    <c-electronic-instruction-search
      v-model:ref-query="query"
      v-model:term-query="termQuery"
      :class="b('search')"
      :reference-example-url="referenceExampleUrl"
      @submit="onSubmit"
      @reset="onReset"
      @error="onError"
    >
      <template
        #[errorSlot]="data"
        v-if="previousQuery && !eifuStore.runningRequests.apiFetchElectronicInstructions && !eifuStore.entries.length"
      >
        <i18n-t
          :keypath="noResultMessage"
          scope="global"
        >
          <template #searchUrl>
            <a :href="data.searchUrl">
              {{ matchThemeTranslation(themeConfig.translations.searchUrlLabel) }}
            </a>
          </template>
        </i18n-t>
      </template>
    </c-electronic-instruction-search>

    <div
      v-if="showBrandTiles && eifuStore.brands.length"
      :class="b('brands-container')"
    >
      <h2 :class="b('brands-heading')">
        {{ $t('c-electronic-instruction-search.searchByProductTile') }}
      </h2>
      <c-electronic-instruction-brand-tiles
        :brands="eifuStore.brands"
        :active-value="brandFilter"
        @selected="onFilterBrandClicked"
      />
    </div>

    <!-- TODO: apply styles and update translations -->
    <div :class="b('legal-notice')">
      {{ matchThemeTranslation(themeConfig.translations.legalNotice) }}
    </div>

    <div
      ref="results"
      :class="b('results')"
    >
      <e-progress
        v-if="eifuStore.runningRequests.apiFetchElectronicInstructions"
        :class="b('progress')"
      />
      <ul
        v-else-if="eifuStore.entries?.length"
        :class="b('list')"
      >
        <li
          v-for="item in eifuStore.entries"
          :key="item.id"
          :class="b('list-item')"
        >
          <h2 :class="b('title')">
            {{ getTitle(item) }}
          </h2>
          <c-electronic-instruction-tile
            :item="item"
            :query="previousQuery"
            border="gradient"
            history
          />
        </li>
      </ul>
    </div>
    <section
      v-if="$slots.left || $slots.right"
      ref="content"
      :class="b('content')"
      class="wysiwyg"
    >
      <div :class="b('content-column', { left: true })">
        <slot name="left"></slot>
      </div>
      <div :class="b('content-column', { right: true })">
        <slot name="right"></slot>
      </div>
    </section>

    <!-- Modals -->
    <c-electronic-instruction-detail
      @click-read-instructions="scrollToContent = true"
      @close="onCloseDetail"
    />
    <c-electronic-instruction-download />
    <c-electronic-instruction-request
      v-model:open="isRequestOpen"
      :item="eifuStore.requestElectronicInstructionItem"
    />
    <c-electronic-instruction-problem
      v-model:open="showProblemForm"
      :query="fileIdForProblemForm || query"
    />

    <c-sticky-toolbar v-if="isTheme('default')" />
  </main>
</template>

<script>
  import { URL_PARAM_MAP } from '@/setup/globals';
  import useEifuStore from '@/stores/eifu';
  import useNotificationStore from '@/stores/notification';
  import cElectronicInstructionBrandTiles from '@/components/c-electronic-instruction-brand-tiles.vue';
  import cElectronicInstructionDetail from '@/components/c-electronic-instruction-detail.vue';
  import cElectronicInstructionDownload from '@/components/c-electronic-instruction-download.vue';
  import cElectronicInstructionProblem from '@/components/c-electronic-instruction-problem.vue';
  import cElectronicInstructionRequest from '@/components/c-electronic-instruction-request.vue';
  import cElectronicInstructionSearch from '@/components/c-electronic-instruction-search.vue';
  import cElectronicInstructionTile from '@/components/c-electronic-instruction-tile.vue';
  import cHeroBanner from '@/components/c-hero-banner.vue';
  import cStickyToolbar from '@/components/c-sticky-toolbar.vue';
  import eProgress from '@/elements/e-progress.vue';
  import themes from '@/mixins/themes.js';

  /**
   * Renders the layout for the electronic instruction list.
   */
  export default {
    name: 'l-electronic-instruction-list',
    components: {
      eProgress,
      cHeroBanner,
      cStickyToolbar,
      cElectronicInstructionProblem,
      cElectronicInstructionDetail,
      cElectronicInstructionDownload,
      cElectronicInstructionSearch,
      cElectronicInstructionRequest,
      cElectronicInstructionTile,
      cElectronicInstructionBrandTiles,
    },
    mixins: [themes],

    props: {
      /**
       * Expects an url to display the reference number example tooltip.
       */
      referenceExampleUrl: {
        type: String,
        required: true,
      },

      /**
       * Expects a picture image definition for the background of the search banner.
       */
      bannerImage: {
        type: Object,
        required: true,
      },

      /**
       * Expects a boolean to determine if the brand tiles should be displayed.
       */
      showBrandTiles: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      return {
        eifuStore: useEifuStore(),
        notificationStore: useNotificationStore(),
      };
    },
    data() {
      return {
        /**
         * @type {String} Holds the current (valid) ref search term.
         */
        query: '',

        /**
         * @type {String} Holds the current term search value.
         */
        termQuery: '',

        /**
         * @type {String} Holds the current brand filter.
         */
        brandFilter: '',

        /**
         * @type {String} Holds the previous search query to not send duplicated requests.
         */
        previousQuery: '',

        /**
         * @type {Boolean} Holds a flag, that determines if the problem report form should be visible.
         */
        showProblemForm: false,

        /**
         * @type {String} Holds a fileId, which will be used for the "report a problem" form.
         */
        fileIdForProblemForm: null,

        /**
         * @type {Object} Holds the last shown notification object.
         */
        activeNotification: null,

        /**
         * @type {Boolean} Flag for scrolling to the custom CMS content.
         */
        scrollToContent: false,

        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          translations: {
            subtitle: {
              default: '',
              sagemax: 'l-sagemax-electronic-instruction-list.subtitle',
            },
            title: {
              default: 'l-electronic-instruction-list.title',
              sagemax: 'l-sagemax-electronic-instruction-list.title',
            },
            teaserText: {
              default: 'l-electronic-instruction-list.teaserText',
              sagemax: 'l-sagemax-electronic-instruction-list.teaserText',
            },
            noResults: {
              default: 'l-electronic-instruction-list.noResults',
              sagemax: 'l-sagemax-electronic-instruction-list.noResults',
            },
            noBrandResults: {
              default: 'l-electronic-instruction-list.noBrandResults',
              sagemax: 'l-sagemax-electronic-instruction-list.noBrandResults',
            },
            searchUrlLabel: {
              default: 'l-electronic-instruction-list.searchUrlLabel',
              sagemax: 'l-sagemax-electronic-instruction-list.searchUrlLabel',
            },
            legalNotice: {
              default: 'l-electronic-instruction-list.legalNotice',
              sagemax: 'l-sagemax-electronic-instruction-list.legalNotice',
            },
          },
          bannerImage: {
            imageInitialHeight: {
              default: 553,
              sagemax: 470,
            },
            position: {
              default: 'right',
              sagemax: 'top',
            },
          },
        },
      };
    },

    computed: {
      /**
       * Return the suitable message for the given search query.
       *
       * @returns {String}
       */
      noResultMessage() {
        // NOTE: noResultMessage is expected to return a translation key, do not use 'matchThemeTranslation'
        return this.query
          ? this.matchTheme(this.themeConfig.translations.noResults)
          : this.matchTheme(this.themeConfig.translations.noBrandResults);
      },

      /**
       * Returns the error slot based on the last performed search.
       *
       * @returns {String}
       */
      errorSlot() {
        return this.query ? 'default' : 'brand';
      },

      /**
       * Returns computed modifier classes.
       *
       * @returns {Object}
       */
      modifiers() {
        const { theme } = this;

        return { theme };
      },

      /**
       * Handles the open state of the model.
       * `v-bind` needs a reactive model, so although we don't process the value we need to supply a reactive boolean.
       *
       * @returns {Boolean}
       */
      isRequestOpen: {
        get() {
          return !!this.eifuStore.requestElectronicInstructionItem;
        },
      },
    },
    watch: {
      /**
       * Resets the specific fileId reference, when the problem form is closed.
       *
       * @param {Boolean} show - Determines the modal state.
       */
      showProblemForm(show) {
        if (!show) {
          this.fileIdForProblemForm = null;
        }
      },
    },

    // beforeCreate() {},
    created() {
      this.$mitt.on('c-electronic-instruction-tile.reportProblem', (file) => {
        this.fileIdForProblemForm = file.fileId;
        this.showProblemForm = true;
      });

      if (this.showBrandTiles) {
        this.eifuStore.apiFetchBrands();
      }
    },
    // beforeMount() {},
    mounted() {
      const search = new URLSearchParams(window.location.search);
      const documentId = search.get(URL_PARAM_MAP.documentId);
      const termQuery = search.get(URL_PARAM_MAP.eifuProductSearch);
      const brandFilter = search.get(URL_PARAM_MAP.eifuBrand);

      if (documentId) {
        this.searchDocument(documentId);
      } else if (termQuery) {
        this.termQuery = termQuery;

        this.search(termQuery, 'brand'); // TODO: Rename to product in BE.
      } else if (brandFilter) {
        this.brandFilter = brandFilter;

        this.search(brandFilter, 'brandFilter');
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onFilterBrandClicked(brandLabel) {
        this.brandFilter = brandLabel;
        this.query = '';
        this.termQuery = '';

        this.search(brandLabel, 'brand', true);
      },

      /**
       * Handler for electronic instruction search.
       *
       * @param {String} query - The given reference number.
       * @param {String} [type = 'number'] - Defines the type of the to be performed search.
       * @param {Boolean} [exact = 'number'] - Defines if the search should return only exact matches.
       */
      search(query, type = 'ref', exact = false) {
        if (query === this.previousQuery) {
          return;
        }

        const encodedQuery = encodeURIComponent(query);

        this.eifuStore
          .apiFetchElectronicInstructions({ query: encodedQuery, type, exact: exact ? 1 : 0 })
          .then((response) => {
            const { data } = response.data;
            const firstResult = Array.isArray(data) && data?.[0];

            if (!firstResult) {
              this.onError();

              return;
            }

            if (firstResult.isHistorical) {
              /**
               * @event l-electronic-instruction-list.focusDocument - Called if query is only available in historical data.
               *
               * @param {Object} payload - The events payload
               * @param {Object} payload.component - The Vue component that emitted the event.
               * @param {Object} payload.document - The document definition that should be focused.
               */
              this.$mitt.emit('l-electronic-instruction-list.focusDocument', {
                component: this,
                document: firstResult,
              });

              this.notificationStore.showNotification({
                delay: 6,
                message: {
                  type: 'warning',
                  message: this.$t('l-electronic-instruction-list.onlyHistoricalResults'),
                },
              });
            } else {
              this.scrollToList();
            }
          });

        this.previousQuery = query;
      },

      /**
       * Callback for the submit event of the search banner.
       *
       * @param {Boolean} error - Defines if the current query is invalid or not.
       * @param {Boolean} suggestion - Defines if the current query results from clicking a suggestion.
       */
      onSubmit(error, suggestion) {
        const { termQuery } = this;

        this.brandFilter = null;

        if (error) {
          this.onReset();
        } else if (termQuery) {
          this.search(termQuery, 'brand', suggestion);
        } else {
          this.search(this.query);
        }
      },

      /**
       * Handles the reset event of the search banner.
       */
      onReset() {
        this.previousQuery = null;
        this.eifuStore.entries = [];
      },

      /**
       * Handles detail modal close.
       */
      onCloseDetail() {
        if (this.scrollToContent) {
          this.scrollToContent = false;

          this.$nextTick(() => {
            // We have to wait for the modal to be closed before scrolling to the custom content.
            this.$refs.content?.scrollIntoView({ behavior: 'smooth' });
          });
        }
      },

      /**
       * Scrolls the viewport to the top of the list.
       */
      scrollToList() {
        this.$nextTick(() => {
          // Wait for results sections to be rendered.
          this.$refs.results?.scrollIntoView({ behavior: 'smooth' });
        });
      },

      /**
       * Executes the search for a specific document.
       *
       * @param {String} documentId - The ID of the desired document.
       */
      searchDocument(documentId) {
        this.eifuStore
          .apiFetchElectronicInstructionByDocumentId(documentId)
          .catch(() => {
            this.onError();
          })
          .finally(this.scrollToList);
      },

      /**
       * Gets the title for an electronic instruction item.
       *
       * @param {Object} item - Expects an electronic instruction item.
       *
       * @returns {String}
       */
      getTitle(item) {
        const { languages } = item || {};

        // 'named' is needed: https://vue-i18n.intlify.dev/guide/essentials/pluralization#predefined-implicit-arguments
        return this.$t('l-electronic-instruction-list.languageVersion', languages?.length || 0, {
          named: {
            language: languages?.[0] || '',
          },
        });
      },

      /**
       * Handles error events with the file search.
       */
      onError() {
        if (!this.notificationStore.getNotifications.includes(this.activeNotification)) {
          const key = this.query
            ? 'l-electronic-instruction-list.noResultsNotification'
            : 'l-electronic-instruction-list.noBrandResultsNotification';

          this.notificationStore
            .showNotification({
              delay: 15,
              message: {
                type: 'warning',
                message: this.$t(key),
              },
              action: {
                callback: () => {
                  this.showProblemForm = true;
                },
                label: this.$t('l-electronic-instruction-list.reportProblemLabel'),
              },
            })
            .then((notification) => {
              this.activeNotification = notification;
            });
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .l-electronic-instruction-list {
    $this: &;

    @extend %layout;

    &--theme-sagemax {
      #{$this}__banner {
        padding: functions.rem(0 0 variables.$spacing--30);

        .c-hero-banner__image {
          display: none;

          @include mixins.media(sm) {
            display: block;
          }
        }

        .c-hero-banner__container {
          justify-content: flex-start;
          padding: functions.rem(variables.$spacing--30) 0;
          border: 0;
        }

        .c-hero-banner__prefix {
          position: relative;
          padding-bottom: functions.rem(variables.$spacing--15);

          &::after {
            position: absolute;
            bottom: 0;
            left: 0;
            content: '';
            width: functions.rem(40px);
            height: functions.rem(2px);
            background: functions.get-theme-var('color-grayscale-0');
          }
        }
      }

      #{$this}__search-prefix {
        padding-bottom: functions.rem(variables.$spacing--5);
        border-bottom: 1px solid functions.get-theme-var('color-grayscale-0');

        &-title {
          @include mixins.font(variables.$font-size--16, variables.$line-height--28);

          margin-bottom: 0;
          color: functions.get-theme-var('color-grayscale-0');
        }
      }

      #{$this}__title {
        @include mixins.font(variables.$font-size--16, variables.$line-height--28);

        margin-bottom: functions.rem(variables.$spacing--40);
        padding-bottom: functions.rem(variables.$spacing--10);
        border-bottom: 1px solid variables.$color-grayscale--0;

        @include mixins.media(md) {
          @include mixins.font(variables.$font-size--16, variables.$line-height--28);
        }
      }
    }

    &__banner {
      .c-hero-banner__image {
        display: none;

        @include mixins.media(sm) {
          display: block;
        }
      }

      .c-hero-banner__container {
        @include mixins.media($down: sm) {
          padding: functions.rem(variables.$spacing--20);
        }
      }

      .c-hero-banner__text-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }

    &__brands-container,
    &__search {
      margin-top: functions.rem(variables.$spacing--50);

      @include mixins.media(sm) {
        margin-top: functions.rem(variables.$spacing--80);
      }

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$spacing--100);
      }
    }

    &__brands-heading {
      margin-bottom: variables.$spacing--40;
    }

    &__results {
      @extend %scroll-margin-top;

      margin-top: functions.rem(variables.$spacing--90);

      @include mixins.media(sm, sm) {
        margin-top: 0;
      }

      &:empty {
        display: none;
      }
    }

    &__progress {
      width: functions.rem(25px);
      height: functions.rem(25px);
      margin: functions.rem(variables.$spacing--80 auto);
    }

    &__content {
      @extend %scroll-margin-top;

      display: grid;
      gap: functions.rem(variables.$spacing--50 0);
      margin-top: functions.rem(variables.$spacing--90);

      @include mixins.media(sm, sm) {
        margin-top: 0;
      }

      @include mixins.media(md) {
        grid-template-columns: repeat(2, 1fr);
        gap: functions.rem(0 (2 * 150px)); // Match gap on c-electronic-instruction-search
      }
    }

    &__content-column {
      overflow-x: auto; // Added because demo data did overflow. If this is causing trouble in production, remove it.
    }

    &__list {
      @extend %list-reset;
    }

    &__list-item {
      margin-bottom: functions.rem(variables.$spacing--70);

      > .c-electronic-instruction-tile {
        margin-bottom: functions.rem(variables.$spacing--15);
      }
    }

    &__title {
      @include mixins.font(variables.$font-size--24, variables.$line-height--28);

      margin-bottom: functions.rem(variables.$spacing--25);

      @include mixins.media(md) {
        @include mixins.font(variables.$font-size--32, variables.$line-height--38);
      }
    }

    &__legal-notice {
      @include mixins.font-size(variables.$font-size--20);

      width: max-content;
      max-width: 100%;
      margin: variables.$spacing--90 auto;
      padding: functions.rem(variables.$spacing--20 variables.$spacing--40);
      background: rgba(functions.get-theme-var('color-primary-2-rgba'), 0.1);
      text-align: center;
    }
  }
</style>
